import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useHookFormMask } from "use-mask-input";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

import "../../../components/forms/forms.css";
import Button from "../../../components/button/button";
import { PHONE_REGEX } from "../../../utils/pagesMapper";
import ErrorMessage from "../../../components/forms/errorMessage/errorMessage";
import LandmarkBlue from "../../../assets/icons/landmark-blue.svg";
import CheckedCircle from "../../../assets/icons/checked-circle.svg";
import HeaderTitle from "../../../components/header/headerTitle/headerTitle";
import TriangleExclamation from "../../../assets/icons/triangle-exclamation.svg";
import { createInstitution } from "../../../services/academetrics";
import { useMantenedora } from "../../../context/MantenedoraContext";

const CEP_REGEX = /^\d{5}-\d{3}$/;

const NewInstitution = () => {
  const navigate = useNavigate();
  const { loadInstitutions, mantenedora, isPlanContracted } = useMantenedora();

  const {
    register,
    setValue,
    handleSubmit,
    formState: { errors }
  } = useForm();

  const registerWithMask = useHookFormMask(register);

  const [ufs, setUfs] = useState([]);
  const [loading, setLoading] = useState(false);

  const getUfs = () => {
    fetch("https://servicodados.ibge.gov.br/api/v1/localidades/estados")
      .then((response) => response.json())
      .then((data) => setUfs(data));
  };

  const getCepInfo = (zipCode) => {
    if (!CEP_REGEX.test(zipCode)) return;

    fetch(`https://viacep.com.br/ws/${zipCode}/json/`)
      .then((response) => response.json())
      .then((data) => {
        setValue("address", data.logradouro);
        setValue("city", data.localidade);
        setValue("state", data.uf);
        setValue("country", "Brasil");
      });
  };

  useEffect(() => {
    getUfs();
  }, []);

  const onSubmit = async (data) => {
    setLoading(true);

    try {
      // Prepara os dados para envio, convertendo valores vazios para null
      const payload = {
        ...data,
        number: data.number ? parseInt(data.number, 10) : 0,
        employeesCount: data.employeesCount ? parseInt(data.employeesCount, 10) : null,
        averageStudents: data.averageStudents ? parseInt(data.averageStudents, 10) : null,
        averageMonthlyTicket: data.averageMonthlyTicket || null,
        complement: data.complement || null,
      };

      await createInstitution(payload);
      
      // Recarrega a lista de instituições da mantenedora
      await loadInstitutions();

      toast.success("Instituição criada com sucesso!");
      setLoading(false);

      // Redireciona para contratação somente quando confirmado pelo backend que não há contrato
      if (isPlanContracted === false) {
        navigate("/app/contratar-plano");
      } else {
        navigate("/app/instituicoes");
      }
    } catch (error) {
      console.log("@error", error);
      toast.error("Erro ao criar instituição.");
      setLoading(false);
    }
  };

  return (
    <div>
      <HeaderTitle title="Nova Instituição" />

      <div className="mainContentSpace">
        <div className="institution-tab">
          <img src={LandmarkBlue} alt="Instituição" />
          <span>Nova Instituição para {mantenedora?.name}</span>
        </div>

        <div className="institution-form">
          <div className="warning-message" style={{ backgroundColor: "#e8f4fd", borderColor: "#0962ad" }}>
            <img src={TriangleExclamation} alt="Info" style={{ filter: "brightness(0) saturate(100%) invert(30%) sepia(68%) saturate(1234%) hue-rotate(182deg) brightness(91%) contrast(94%)" }} />
            <span style={{ color: "#0962ad" }}>
              Preencha os dados abaixo para criar uma nova instituição vinculada à mantenedora.
            </span>
          </div>

          <form onSubmit={handleSubmit(onSubmit)}>
            {/* SEÇÃO: DADOS DA INSTITUIÇÃO */}
            <div className="form-section">
              <div className="form-section-header">
                <img src={LandmarkBlue} alt="Dados" />
                <h3>Dados da Instituição</h3>
              </div>

              <fieldset>
                <legend>Informações Básicas</legend>

                <div className="form-row" style={{ alignItems: "flex-start" }}>
                  <div className="form-group">
                    <input
                      id="name"
                      name="name"
                      placeholder="Nome da Instituição"
                      {...register("name", { required: "O nome é obrigatório." })}
                    />
                    <ErrorMessage message={errors.name?.message} />
                  </div>

                  <div className="form-group">
                    <select
                      id="institutionType"
                      name="institutionType"
                      {...register("institutionType", {
                        required: "O tipo é obrigatório."
                      })}
                    >
                      <option value="">Selecione o tipo</option>
                      <option value="University">Universidade</option>
                      <option value="School">Escola</option>
                      <option value="Other">Outro</option>
                    </select>
                    <ErrorMessage message={errors.institutionType?.message} />
                  </div>
                </div>
              </fieldset>

              <fieldset>
                <legend>Endereço</legend>

                <div className="form-row">
                  <div className="form-group">
                    <input
                      id="zipCode"
                      name="zipCode"
                      placeholder="CEP"
                      {...registerWithMask("zipCode", ["99999-999"], {
                        required: "O CEP é obrigatório.",
                        pattern: {
                          value: CEP_REGEX,
                          message: "O CEP é inválido."
                        },
                        onBlur: (e) => getCepInfo(e.target.value)
                      })}
                    />
                    <ErrorMessage message={errors.zipCode?.message} />
                  </div>

                  <div className="form-group">
                    <input
                      id="address"
                      name="address"
                      placeholder="Rua"
                      {...register("address", { required: "A rua é obrigatório." })}
                    />
                    <ErrorMessage message={errors.address?.message} />
                  </div>

                  <div className="form-group">
                    <input
                      id="number"
                      name="number"
                      placeholder="Nº"
                      {...register("number", {
                        required: "O número é obrigatório."
                      })}
                    />
                    <ErrorMessage message={errors.number?.message} />
                  </div>

                  <div className="form-group">
                    <input
                      id="complement"
                      name="complement"
                      placeholder="Complemento"
                      {...register("complement")}
                    />
                    <ErrorMessage message={errors.complement?.message} />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <input
                      id="city"
                      name="city"
                      {...register("city", { required: "A cidade é obrigatória." })}
                      placeholder="Cidade"
                    />
                    <ErrorMessage message={errors.city?.message} />
                  </div>

                  <div className="form-group">
                    <select
                      id="state"
                      name="state"
                      {...register("state", { required: "A UF é obrigatória." })}
                    >
                      <option value="">UF</option>
                      {ufs.map((uf) => (
                        <option key={uf.sigla} value={uf.sigla}>
                          {uf.nome}
                        </option>
                      ))}
                    </select>
                    <ErrorMessage message={errors.state?.message} />
                  </div>

                  <div className="form-group">
                    <select
                      id="country"
                      name="country"
                      {...register("country", {
                        required: "O país é obrigatório."
                      })}
                    >
                      <option value="">País</option>
                      <option value="Brasil">Brasil</option>
                    </select>
                    <ErrorMessage message={errors.country?.message} />
                  </div>
                </div>
              </fieldset>

              <fieldset>
                <legend>Contato</legend>

                <div className="form-row">
                  <div className="form-group">
                    <input
                      id="email"
                      name="email"
                      placeholder="E-mail"
                      {...register("email", {
                        required: "O e-mail é obrigatório.",
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                          message: "O e-mail é inválido."
                        }
                      })}
                    />
                    <ErrorMessage message={errors.email?.message} />
                  </div>

                  <div className="form-group">
                    <input
                      id="phone"
                      name="phone"
                      placeholder="Telefone"
                      {...registerWithMask("phone", ["(99) 99999-9999"], {
                        required: "O telefone é obrigatório.",
                        pattern: {
                          value: PHONE_REGEX,
                          message: "O telefone é inválido."
                        }
                      })}
                    />
                    <ErrorMessage message={errors.phone?.message} />
                  </div>
                </div>
              </fieldset>

              <fieldset>
                <legend>Dados Operacionais (Opcional)</legend>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="employeesCount">Quantidade de Funcionários</label>
                    <input
                      id="employeesCount"
                      name="employeesCount"
                      type="number"
                      min="1"
                      placeholder="Ex: 50"
                      {...register("employeesCount", {
                        min: { value: 1, message: "Mínimo de 1 funcionário." }
                      })}
                    />
                    <ErrorMessage message={errors.employeesCount?.message} />
                  </div>

                  <div className="form-group">
                    <label htmlFor="averageStudents">Quantidade Média de Alunos</label>
                    <input
                      id="averageStudents"
                      name="averageStudents"
                      type="number"
                      min="1"
                      placeholder="Ex: 500"
                      {...register("averageStudents", {
                        min: { value: 1, message: "Mínimo de 1 aluno." }
                      })}
                    />
                    <ErrorMessage message={errors.averageStudents?.message} />
                  </div>

                  <div className="form-group">
                    <label htmlFor="averageMonthlyTicket">Ticket Médio Mensal</label>
                    <input
                      id="averageMonthlyTicket"
                      name="averageMonthlyTicket"
                      type="text"
                      placeholder="R$ 0,00"
                      {...registerWithMask("averageMonthlyTicket", "currency", {
                        prefix: "R$ ",
                        radixPoint: ",",
                        groupSeparator: ".",
                        digits: 2,
                        digitsOptional: false,
                        allowMinus: false
                      })}
                    />
                    <ErrorMessage message={errors.averageMonthlyTicket?.message} />
                  </div>
                </div>
              </fieldset>
            </div>

            <div className="form-actions">
              <Button
                text="Cancelar"
                variation="outline"
                size="big"
                onClick={() => navigate("/app/instituicoes")}
                type="button"
              />
              <Button
                text="Criar Instituição"
                variation="primary"
                size="big"
                icon={CheckedCircle}
                loading={loading}
              />
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default NewInstitution;
